<script lang="ts" setup>
import { computed, onBeforeUnmount } from 'vue'
import { useChangelogQuery } from '@/api/changelog'
import { TelemetryEvent, useTelemetry } from '@/common/useTelemetry'
import { formatDateWithYear } from '@/utils/date-formatting'
import { getInstance } from '@/auth'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const auth = getInstance()
const enabled = computed(() => auth?.isAuthenticated ?? false)
const { trackEvent } = useTelemetry()
const { data, isLoading: loading, isError: error, refetch } = useChangelogQuery(enabled)
const entries = computed(() => data.value ?? [])
const changelogOpenedAt = performance.now()

function handleDialogInput(value: boolean) {
  if (!value) {
    emit('close')
  }
}

onBeforeUnmount(() => {
  trackEvent(
    TelemetryEvent.ChangelogVisited,
    {},
    {
      durationMs: Math.round(performance.now() - changelogOpenedAt),
    },
  )
})
</script>

<template>
  <v-dialog :value="true" max-width="700" scrollable @input="handleDialogInput">
    <v-card class="changelog-card">
      <v-card-title class="changelog-header">
        <span class="changelog-title">What’s New</span>
        <v-btn icon small @click="handleDialogInput(false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="changelog-body">
        <!-- Loading state -->
        <div v-if="loading" class="changelog-loading">
          <div v-for="i in 3" :key="i" class="changelog-skeleton">
            <div class="skeleton-badge" />
            <div class="skeleton-title" />
            <div class="skeleton-line" />
            <div class="skeleton-line skeleton-line--short" />
          </div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="changelog-error">
          <v-icon color="grey" large>error_outline</v-icon>
          <p class="changelog-error-text">Unable to load release notes.</p>
          <v-btn small outlined color="primary" @click="refetch"> Retry </v-btn>
        </div>

        <!-- Empty state -->
        <div v-else-if="entries.length === 0" class="changelog-empty">
          <v-icon color="grey" large>info_outline</v-icon>
          <p class="changelog-empty-text">No release notes available yet.</p>
        </div>

        <!-- Entries timeline -->
        <div v-else class="changelog-timeline">
          <div v-for="entry in entries" :key="entry.id" class="changelog-entry">
            <div class="changelog-entry-header">
              <span class="changelog-date-badge">
                {{ formatDateWithYear(entry.publish_date) }}
              </span>
              <h3 class="changelog-entry-title">{{ entry.title }}</h3>
            </div>
            <div v-if="entry.video_url" class="video-container">
              <video controls loading="lazy">
                <source :src="entry.video_url" type="video/mp4" />
              </video>
            </div>
            <div class="changelog-entry-content" v-html="entry.html_content" />
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.changelog-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.changelog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.changelog-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.changelog-body {
  padding: 0 20px 20px;
  max-height: 70vh;
  overflow-y: auto;
}

/* Loading skeleton */
.changelog-loading {
  padding-top: 16px;
}

.changelog-skeleton {
  margin-bottom: 32px;
}

.skeleton-badge {
  width: 120px;
  height: 14px;
  background: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-title {
  width: 60%;
  height: 20px;
  background: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 12px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-line {
  width: 100%;
  height: 12px;
  background: #eeeeee;
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-line--short {
  width: 75%;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Error & empty states */
.changelog-error,
.changelog-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
}

.changelog-error-text,
.changelog-empty-text {
  margin: 12px 0;
  color: #757575;
  font-size: 14px;
}

/* Timeline entries */
.changelog-timeline {
  padding-top: 16px;
}

.changelog-entry {
  position: relative;
  padding-bottom: 28px;
  margin-bottom: 28px;
  border-bottom: 1px solid #f0f0f0;
}

.changelog-entry:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.changelog-entry-header {
  margin-bottom: 12px;
}

.changelog-date-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 10px;
  border-radius: 12px;
  margin-bottom: 6px;
}

.changelog-entry-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 4px 0 0;
}

.video-container {
  & video {
    max-width: 100%;
  }
}

.changelog-entry-content {
  max-width: 65ch;
}
</style>

<style>
/* Unscoped styles for rendered HTML content */
.changelog-entry-content {
  font-size: 14px;
  line-height: 1.7;
  color: #374151;
}

.changelog-entry-content h1,
.changelog-entry-content h2,
.changelog-entry-content h3,
.changelog-entry-content h4 {
  font-weight: 600;
  margin: 16px 0 8px;
  color: #1a1a1a;
}

.changelog-entry-content h2 {
  font-size: 15px;
}

.changelog-entry-content h3 {
  font-size: 14px;
}

.changelog-entry-content p {
  margin: 8px 0;
}

.changelog-entry-content ul,
.changelog-entry-content ol {
  padding-left: 20px;
  margin: 8px 0;
}

.changelog-entry-content li {
  margin: 4px 0;
}

.changelog-entry-content img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  margin: 12px 0;
  float: none !important;
}

.changelog-entry-content a {
  color: #2563eb;
  text-decoration: none;
}

.changelog-entry-content a:hover {
  text-decoration: underline;
}

.changelog-entry-content code {
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.changelog-entry-content blockquote {
  border-left: 3px solid #e5e7eb;
  padding-left: 12px;
  margin: 8px 0;
  color: #6b7280;
}
</style>
